<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <hero-bar>
      Report Test List
      <div slot="right">
        <b-button
          v-if="isEditAllowed"
          type="is-primary import-button"
          @click="generateTestRun"
          >+ &nbsp;Generate a Test Run &nbsp;</b-button
        >
      </div>
    </hero-bar>
    <section class="section is-main-section">
      <div class="level-right" v-if="!viewOnlyUser">
        <div
          class="level-left current-report-default-view-with-icon"
          v-if="!isUserExternal"
        >
          <span class="current-report-default-view-text">
            <b-field label="My Customers"></b-field>
          </span>
          <span class="additonal-info">
            <b-tooltip
              label="Shows data of customers where logged in user is added as CAM or Back-up CAM"
              type="is-primary is-light"
              position="is-left"
              multilined
              data-cy="rt-additonal-info"
            >
              <i class="mdi mdi-information" />
            </b-tooltip>
          </span>
        </div>
        <b-switch
          name="default_view"
          :checked="true"
          @input="showCamUserView"
          v-model="showCamUserData"
          class="current-report-default-view-switch"
          data-cy="rt-switch-reports"
        />
      </div>
      <circul8-table
        :records="data"
        :fields="columns"
        :usePropRecords="true"
        :listingComponent="true"
        :routeNext="false"
        :isCheckable="false"
        :maxTextLength="'24'"
        :isLoading="isDataLoading"
        :backendSortingEnabled="true"
        @backendSort="sortData"
        @querySet="searchByFilter"
        @loadNextPage="pullNextPage"
        :enableInfiniteScroll="true"
        :hasNextData="nextData"
        :hasPreviousData="previousData"
        @loadPreviousPage="pullPreviousPage"
        @clickView="clickViewTestRun"
        :allCustomers="customers"
        :saveDataFields="true"
        :tableName="tblName"
      />
      <div class="records-displayed" v-if="totalRecords > 0">
        <span v-if="currentPage * 50 + 50 < totalRecords"
          >displaying {{ currentPage > 1 ? currentPage * 50 - 50 : 0 }} -
          {{
            currentPage > 0
              ? nextPageCursor
                ? currentPage * 50 - 50 + data.length
                : totalRecords
              : data.length
          }}
          records out of {{ totalRecords }} records</span
        >
        <div v-else>
          <span v-if="currentPage == 0">
            displaying 0 - {{ totalRecords }} records out of
            {{ totalRecords }} records
          </span>
          <span v-else>
            displaying {{ currentPage * 50 - 50 }} - {{ totalRecords }} records
            out of {{ totalRecords }} records
          </span>
        </div>
      </div>
    </section>
    <generate-test-run-modal
      :is-active="isModalActive"
      :parent-customer="customers"
      :schemeListArray="schemes"
      :countryListArray="countries"
      :containerListArrat="containers"
      @confirm="runTest"
      @cancel="closeGenerateTestRunModal"
    />
    <urgency-modal
      :is-active="isUrgencyModalActive"
      @confirm="runTest"
      @cancel="closeUrgencyModal"
    />
  </div>
</template>

<script>
import TitleBar from "@/components/TitleBar";
import HeroBar from "@/components/HeroBar";
import Circul8Table from "../../components/Circul8Table.vue";
import RulesManagerService from "../../services/RulesManagerService";
import { mapGetters } from "vuex";
import { forkJoin } from "rxjs";
import UtilityMixin from "@/mixins/UtilityMixin";
import ScopeMixin from "@/mixins/scopeMixin";
import { TestRunStatus } from "../../constants/TestRunStatus";
import { UserGroup } from "@/services/UserGroups";
import ReportManagerService from "../../services/ReportManagerDataService";
import CustomerProfileDataService from "../../services/CustomerProfileDataService";
import GenerateTestRunModal from "@/components/ReportsManager/GenerateTestRunModal.vue";
import UrgencyModal from "@/components/ReportsManager/UrgencyModal.vue";

export default {
  name: "ReportDefinitions",
  mixins: [UtilityMixin, ScopeMixin],
  components: {
    HeroBar,
    TitleBar,
    Circul8Table,
    GenerateTestRunModal,
    UrgencyModal
  },
  data() {
    return {
      tblName: "TestingList",
      data: [],
      showCamUserData: false,
      isDataLoading: false,
      isModalActive: false,
      isUrgencyModalActive: false,
      editObject: this.getClearFormObject(),
      nextPageCursor: "",
      PreviousPageCursor: "",
      loadMoreClicked: false,
      lastSortColumn: null,
      lastSortOrder: null,
      enableLoadMore: false,
      totalRecords: 0,
      isLoading: false,
      nextData: false,
      previousData: false,
      currentPage: 0,
      query: "",
      customers: [],
      columns: [
        {
          field: "customer_name",
          label: "Customer Name",
          searchable: true,
          type: "multiSelectFilter",
          searchKey: "customer_name",
          visible: true,
          allStatusVals: this.customers,
          initStatusVals: [],
          multiSelectFilter: true,
          filterKey: { customer_name: [] }
        },
        {
          field: "country_name",
          label: "Country",
          searchable: true,
          searchKey: "country_name",
          type: "multiSelectFilter",
          multiSelectFilter: true,
          initStatusVals: [],
          allStatusVals: this.countries,
          filterKey: { country_name: [] }
        },

        {
          field: "scheme_name",
          label: "Scheme",
          type: "multiSelectFilter",
          searchable: true,
          filterKey: { scheme_name: [] },
          allStatusVals: this.schemes,
          searchKey: "scheme_name"
        },
        {
          field: "stream_name",
          label: "Stream",
          searchable: true,
          type: "multiSelectFilter",
          filterKey: { stream_name: [] },
          initStatusVals: [],
          allStatusVals: this.containers,
          searchKey: "stream_name"
        },
        {
          field: "name",
          label: "Report Definition",
          searchable: true,
          filterKey: { name: "" },
          type: "input"
        },
        {
          field: "status",
          label: "Test Status",
          type: "multiSelectFilter",
          searchable: true,
          visible: true,
          initStatusVals: [],
          allStatusVals: [
            { name: "Calculated" },
            { name: "Calculation In Progress" },
            { name: "Failed" },
            { name: "Scheduled" }
          ],
          filterKey: {
            status: [],
            field_name: "name"
          }
        },
        {
          field: "period",
          label: "Period",
          searchable: true,
          filterKey: { period: "" },
          type: "input"
        },
        {
          field: "generation_date",
          label: "Generation Date",
          searchable: true,
          filterKey: { generation_date: "" },
          type: "dateField"
        },
        {
          field: "generated_by_name",
          label: "Generated By",
          searchable: true,
          filterKey: { generated_by_name: "" },
          type: "input"
        },
        {
          field: "failure_details",
          label: "Failure Details",
          searchable: true,
          filterKey: { failure_details: "" },
          type: "input",
          additionalInfo: true,
          additionalInfoIcon: "mdi  mdi-information",
          additionalInfoFieldName: "failure_details"
        },
        {
          field: "actions",
          label: "",
          headerClass: "is-actions",
          cellClass: "is-actions",
          visible: true,
          searchable: true,
          type: "actionSearch",
          actionSearch: true,
          filterKey: { actions: "" }
        }
      ],
      RulesManagerService: new RulesManagerService(),
      customerDataService: new CustomerProfileDataService(),
      ReportManagerService: new ReportManagerService()
    };
  },
  mounted() {
    this.checkExternalPermission();
    this.fetchCountries();
    this.fetchSchemes();
    this.fetchContainers();
    forkJoin(this.fetchCustomerData()).subscribe(parents => {
      if (localStorage.getItem(this.tblName + "SaveDataFields")) {
        this.columns = JSON.parse(
          localStorage.getItem(this.tblName + "SaveDataFields")
        );
        // convert date string to object to auto populate in filter for date
        this.convertDateStringToObject();
      }
      if (localStorage.getItem(this.tblName + "Query")) {
        this.query = `${localStorage.getItem(this.tblName + "Query")}&`;
      }
      if (localStorage.getItem("showCamUserData")) {
        this.showCamUserData = localStorage.getItem("showCamUserData");
        this.query = this.query + "cam_user" + "=" + this.showCamUserData + "&";
      }
      this.getGeneratedTestRun(this.query);
    });
  },
  computed: {
    ...mapGetters([
      "userId",
      "userName",
      "userHasPermission",
      "userGroups",
      "isUserExternal"
    ]),
    viewOnlyUser() {
      return this.userGroups.includes(UserGroup.VIEW_ONLY);
    },
    editObjectName() {
      return this.editObject || this.getClearFormObject();
    },
    titleStack() {
      return [
        { url: "#", name: "Report Manager" },
        { url: "#", name: "Report Test List" }
      ];
    },
    isEditAllowed() {
      if (
        this.userGroups.includes(UserGroup.EPR_ADMIN) ||
        this.userGroups.includes(UserGroup.CUSTOMER_ACCOUNT_MANAGER)
      ) {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    showCamUserView(value) {
      this.data = [];
      this.map1 = new Map();
      this.checkedRows = [];
      this.isCheckedAll = false;
      this.checkedData = [];
      this.checkedDataMap = new Map();
      // check includes and replace
      this.query = this.query
        .replace(/cam_user=[^&]*&?/g, "")
        .replace(/&$/, "");
      this.query = this.query + "&cam_user=" + this.showCamUserData;

      if (this.query.includes("page=")) {
        // replace pagination page parameter from query string
        this.query = this.query.replace(/page=[0-9.]{1,2}&/g, "");
      }
      this.loadMoreClicked = false;
      this.nextPageCursor = null;
      this.PreviousPageCursor = null;
      this.displayedRecords = 0;
      this.currentPage = 0;
      this.nextData = false;
      this.getGeneratedTestRun(this.query);
      localStorage.setItem("showCamUserData", value);
    },
    convertDateStringToObject() {
      const generation_date = this.find_column_index("generation_date");
      if (this.columns[generation_date].filterKey.generation_date.length) {
        this.columns[generation_date].filterKey.generation_date[0] = new Date(
          this.columns[generation_date].filterKey.generation_date[0]
        );
        this.columns[generation_date].filterKey.generation_date[1] = new Date(
          this.columns[generation_date].filterKey.generation_date[1]
        );
      }
    },
    closeUrgencyModal() {
      this.editObject = {};
      this.isUrgencyModalActive = false;
    },
    closeGenerateTestRunModal() {
      this.isModalActive = false;
    },
    checkUrgency(editObject) {
      this.isModalActive = false;
      this.editObject = editObject;
      this.isUrgencyModalActive = true;
    },
    generateTestRun() {
      this.isModalActive = true;
    },

    getClearFormObject() {
      return {
        customer: null,
        country: null,
        scheme: null,
        stream: null,
        report_definition: null,
        period: null,
        execution_urgency: null
      };
    },

    runTest(editObject) {
      //this.editObject.execution_urgency = editObject;
      this.ReportManagerService.generateTestRun(editObject).subscribe(() => {
        this.$buefy.toast.open({
          message: "Data is added successfully!",
          type: "is-success"
        });
        this.isModalActive = false;
        this.data = [];
        this.getGeneratedTestRun(this.query);
      }, this.handleApiError);
    },

    fetchCustomerData() {
      const customerObservable = this.customerDataService.getParentCustomerListReportRun();
      customerObservable.subscribe(
        response => {
          this.customers = response.sort((a, b) => (a.name > b.name ? 1 : -1));
        },
        e => {
          console.log(e);
        }
      );
      return customerObservable;
    },

    prepareData(data) {
      const dataList = [];
      for (const report in data) {
        const reportData = data[report];
        reportData["valid_to"] = this.dateToLocalDate(reportData["valid_to"]);
        reportData["valid_from"] = this.dateToLocalDate(
          reportData["valid_from"]
        );
        reportData["updated_at"] = this.dateToLocalDate(
          reportData["updated_at"]
        );
        reportData["active"] = reportData["active"] ? "Active" : "Deactive";
        reportData["disabledView"] =
          data[report].status === TestRunStatus.CALCULATED ? false : true;
        reportData["actions"] = data[report]["active"];
        dataList.push(reportData);
      }

      if (this.nextPageCursor == null) {
        this.data.push(...dataList);
        if (this.currentPage > 1) {
          this.currentPage = this.currentPage - 1;
          if (this.currentPage == 1) {
            this.previousData = false;
          }
        } else {
          this.currentPage = this.currentPage - 1;
          this.previousData = false;
        }
      } else {
        this.data = dataList;
      }
      this.isDataLoading = false;
    },
    pullNextPage() {
      if (!this.isDataLoading) {
        this.previousData = true;
        this.loadMoreClicked = true;
        this.currentPage = this.currentPage + 1;
        const temp = this.nextPageCursor.split("?")[1].split("&");
        const query_part = temp.filter(function(value) {
          return value.indexOf("page=") == -1;
        });
        if (this.nextPageCursor != null || this.nextPageCursor != undefined) {
          this.query =
            "&" +
            query_part.join("&") +
            "&page=" +
            (this.currentPage + 1) +
            "&";
          this.getGeneratedTestRun(this.query, "next");
        }
      }
    },
    pullPreviousPage() {
      if (!this.isDataLoading) {
        this.nextData = true;
        this.loadMoreClicked = true;
        const temp = this.PreviousPageCursor.split("?")[1].split("&");
        let query_part = temp.filter(function(value) {
          return value.indexOf("page=") == -1;
        });
        query_part = [...new Set(query_part)];
        if (this.currentPage == 0 || this.currentPage == 1) {
          this.query = "&" + query_part.join("&") + "&page=1&";
          this.previousData = false;
        } else {
          this.query =
            "&" + query_part.join("&") + "&page=" + this.currentPage + "&";
        }
        this.currentPage =
          this.currentPage > 0 ? this.currentPage - 1 : this.currentPage;
        this.getGeneratedTestRun(this.query, "prev");
      }
    },
    searchByFilter(filterQuery) {
      this.nextPageCursor = null;
      this.currentPage = 0;
      this.data = [];
      this.nextData = false;
      if (localStorage.getItem("showCamUserData")) {
        this.showCamUserData = localStorage.getItem("showCamUserData");
      }
      filterQuery += "cam_user" + "=" + this.showCamUserData + "&";
      this.query = filterQuery;

      let query = "";
      if (this.lastSortColumn) {
        query += "&order_field" + "=" + this.lastSortColumn + "&";
        query += "&order_by" + "=" + this.lastSortOrder + "&";
        query += this.query;
      } else {
        query = this.query;
      }
      this.getGeneratedTestRun(query);
    },
    sortData(field, order) {
      this.loadMoreClicked = false;
      this.nextPageCursor = null;
      this.currentPage = 0;
      this.data = [];
      this.nextData = false;
      // sort should always fetch page 1, so removing page param if exist
      this.query = this.query.replace(/page=[0-9.]{1,2}&/g, "");
      if (localStorage.getItem("showCamUserData")) {
        this.showCamUserData = localStorage.getItem("showCamUserData");
      }
      this.query = this.replaceOrAddParanInSearchQuery(
        "cam_user",
        this.showCamUserData
      );
      const oldOrderField = "&order_field" + "=" + this.lastSortColumn + "&";
      const oldOrderValue = "&order_by" + "=" + this.lastSortOrder + "&";
      const newOrderField = "&order_field" + "=" + field + "&";
      const newOrderValue = "&order_by" + "=" + order + "&";

      this.query = this.replaceOrAddParanInSearchQuery(
        oldOrderField,
        newOrderField
      );
      this.query = this.replaceOrAddParanInSearchQuery(
        oldOrderValue,
        newOrderValue
      );

      //     this.displayedRecords = 0;
      this.lastSortColumn = field;
      this.lastSortOrder = order;
      this.getGeneratedTestRun(this.query);
    },

    replaceOrAddParanInSearchQuery(oldParam, newParam) {
      if (this.query.includes(oldParam)) {
        this.query = this.query.replace(oldParam, newParam);
      } else {
        this.query += newParam;
      }
      return this.query;
    },

    clickViewTestRun(editObject) {
      const route = this.$router.resolve({
        path: `/reportManager/testingList/view/${editObject.id}`,
        params: { editObject: editObject }
      });
      window.open(route.href, "_blank");
    },
    getGeneratedTestRun(query = "", scrollDir) {
      this.isDataLoading = true;
      this.ReportManagerService.getGeneratedTestRun(query).subscribe(
        response => {
          this.columns[0].allStatusVals = this.customers;
          // this.columns[1].allStatusVals = this.countries;
          // this.columns[2].allStatusVals = this.schemes;
          // this.columns[3].allStatusVals = this.containers;
          this.PreviousPageCursor = this.nextPageCursor
            ? this.nextPageCursor
            : this.PreviousPageCursor;
          this.nextPageCursor = response["next"];
          if (this.currentPage > 1) {
            this.previousData = true;
            if (this.nextPageCursor != null) {
              this.enableLoadMore = this.nextData = true;
            } else {
              this.enableLoadMore = this.nextData = false;
            }
          } else {
            this.enableLoadMore = this.previousData = false;
            if (this.nextPageCursor != null) {
              this.enableLoadMore = this.nextData = true;
            } else {
              this.enableLoadMore = this.nextData = false;
            }
          }
          this.totalRecords = response["count"];
          let tempData = [];
          if (scrollDir == "next") {
            if (!this.nextPageCursor) {
              this.prepareData(response["results"]);
            } else {
              tempData = [...this.data, ...response["results"]];
              if (tempData.length > 100 && this.nextPageCursor != null) {
                tempData = tempData.slice(50, 150);
              }
              this.prepareData(tempData);
            }
          } else if (scrollDir == "prev") {
            if (this.currentPage == 0) {
              this.prepareData(response["results"]);
            } else {
              tempData = [...response["results"], ...this.data];
              if (tempData.length >= 100) {
                tempData = tempData.slice(0, 100);
              }
              this.prepareData(tempData);
            }
          } else {
            this.prepareData(response["results"]);
          }
          this.isDataLoading = false;
        },
        e => {
          this.isDataLoading = false;
        }
      );
    },
    handleApiError(reason) {
      let errorMessage = null;
      if (reason?.response?.data) {
        if (reason.response.data["detail"]) {
          errorMessage = reason.response.data["detail"];
        } else if (reason.response.data["error"]) {
          errorMessage = reason.response.data["error"];
        } else if (reason.response.data["name"]) {
          errorMessage = reason.response.data["name"];
        } else {
          errorMessage = "Invalid input";
        }
      }
      this.$buefy.dialog.alert({
        title: "Error",
        message: errorMessage,
        type: "is-danger",
        ariaRole: "alertdialog",
        ariaModal: true,
        onConfirm: () => this.getGeneratedTestRun(this.query)
      });
      this.isDataLoading = false;
    }
  }
};
</script>
